// Detects whether the current visitor is likely an app owner.
//
// Signals (any one promotes them to "owner" for 14 days):
//   • URL has ?ref=owner|appstore|founder|premium
//   • URL has ?utm_source=appstore|crunchbase|app-owner|owner-newsletter
//   • URL has ?owner=1
//   • Visit to /sponsor, /submit-app, /list-your-app, /premium-listing, /admin
//
// A dismiss action hides the banner for 7 days.

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const OWNER_KEY = "hac_owner_signal_ts";
const DISMISS_KEY = "hac_owner_banner_dismissed_ts";
const OWNER_TTL_DAYS = 14;
const DISMISS_TTL_DAYS = 7;

const OWNER_PATHS = ["/sponsor", "/submit-app", "/list-your-app", "/premium-listing", "/premium", "/admin"];
const OWNER_REFS = new Set(["owner", "appstore", "founder", "premium", "vendor"]);
const OWNER_UTM_SOURCES = new Set(["appstore", "crunchbase", "app-owner", "owner-newsletter", "app-owners", "founders"]);

const now = () => Date.now();
const daysAgo = (ts) => (now() - Number(ts || 0)) / 86_400_000;

export const useOwnerVisitor = () => {
  const location = useLocation();
  const [, force] = useState(0);

  // Stamp signal whenever an owner path is visited or matching URL param is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = (params.get("ref") || "").toLowerCase();
    const utm = (params.get("utm_source") || "").toLowerCase();
    const owner = params.get("owner");
    const onOwnerPath = OWNER_PATHS.some((p) => location.pathname.startsWith(p));
    const stamp =
      onOwnerPath ||
      owner === "1" ||
      OWNER_REFS.has(ref) ||
      OWNER_UTM_SOURCES.has(utm);
    if (stamp) {
      try {
        localStorage.setItem(OWNER_KEY, String(now()));
        force((n) => n + 1);
      } catch {}
    }
  }, [location.pathname, location.search]);

  const { isOwner, dismissed } = useMemo(() => {
    try {
      const ownerTs = localStorage.getItem(OWNER_KEY);
      const dismissedTs = localStorage.getItem(DISMISS_KEY);
      return {
        isOwner: ownerTs ? daysAgo(ownerTs) < OWNER_TTL_DAYS : false,
        dismissed: dismissedTs ? daysAgo(dismissedTs) < DISMISS_TTL_DAYS : false,
      };
    } catch {
      return { isOwner: false, dismissed: false };
    }
  }, [location.pathname, location.search]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(now())); force((n) => n + 1); } catch {}
  };

  return { isOwner, dismissed, dismiss };
};
